
import moment from 'moment';
import { Vue, Component, Watch } from 'vue-property-decorator';
import FullCalendar from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import SettingsTopBar from '@/modules/CMS/layout/SettingsTopBar.vue';
import NextStep from '@/models/NextStep';
import NextStepRepository from '@/repositories/NextStepRepository';
import NextStepType from '@/models/NextStepType';
import ReminderOption from '@/models/ReminderOption';
import Locale from '@/models/Locale';
import LocaleRepository from '@/repositories/LocaleRepository';
import slLocale from '@fullcalendar/core/locales/sl';
import enLocale from '@fullcalendar/core/locales/en-gb';
import hrLocale from '@fullcalendar/core/locales/hr';
import deLocale from '@fullcalendar/core/locales/de-at';
import huLocale from '@fullcalendar/core/locales/hu';
import itLocale from '@fullcalendar/core/locales/it';
import store from '@/store';
import { UserRightsEnum } from '@/enums/global/UserRights';

@Component({
    name: 'HolidayCalendar',
    components: {
        SettingsTopBar,
        FullCalendar,
    },
})
export default class HolidayCalendar extends Vue {
    private isLoading = false;
    private addHolidayEvent = 'add-new-holiday' as any;
    private calendarOptions = {
        plugins: [dayGridPlugin, interactionPlugin],
        initialView: 'dayGridMonth',
        locale: slLocale,
        editable: false,
        events: [] as any[],
        headerToolbar: {
            start: 'prev,next,today',
            center: 'title',
            end: 'dayGridMonth,dayGridWeek,dayGridDay',
        },
        eventClick: this.handleEventClick,
        dateClick: this.handleDateClick,
    };
    private saveActivityHandler: ((args: any) => Promise<void>) | null = null;
    private updateActivityHandler: ((args: any) => Promise<void>) | null = null;
    private deleteActivityHandler: ((args: any) => Promise<void>) | null = null;

    private get currentLocale() {
        return LocaleRepository.getLocaleByCode(this.$i18n.locale);
    }

    private get canEditActivity() {
        const userRights = store.getters['jwtData/userRights'];
        return userRights.includes(UserRightsEnum.CMS_HOLIDAY_CALENDAR);
    }

    private get canDeleteActivity() {
        const userRights = store.getters['jwtData/userRights'];
        return userRights.includes(UserRightsEnum.CMS_HOLIDAY_CALENDAR);
    }

    private get holidays() {
        return this.$store.getters['tableData/workFreeDays'];
    }

    private async created() {
        this.isLoading = true;
        await this.loadData();
        this.isLoading = false;
    }

    private async loadData() {
        try {
            await Promise.all([Locale.getAll(), NextStepType.getAll(), ReminderOption.getAll()]);

            this.calendarOptions.locale = this.getLocaleForCalendar();
            await this.loadHolidays();
        } catch (e) {
            this.$notification.error({
                message: this.$t('Error loading holidays') as string,
                description: '',
            });
        }
    }

    private async loadHolidays() {
        try {
            await NextStep.getWorkFreeDays();

            if (this.holidays.length) {
                this.calendarOptions.events = this.holidays.map((holiday: any) => ({
                    id: holiday.id,
                    title: holiday.name,
                    start: moment(holiday.dateFrom).format('YYYY-MM-DD'),
                    end: moment(holiday.dateTo).add(1, 'day').format('YYYY-MM-DD'), // FullCalendar end date is exclusive
                    backgroundColor: '#ff4d4f',
                    borderColor: '#ff4d4f',
                    extendedProps: {
                        holidayData: holiday,
                    },
                }));
            }
        } catch (e) {
            this.$notification.error({
                message: this.$t('Error loading holidays') as string,
                description: '',
            });
        }
    }

    private getLocaleForCalendar() {
        if (this.currentLocale) {
            switch (this.currentLocale?.code) {
                case 'sl':
                    return slLocale;
                case 'hr':
                    return hrLocale;
                case 'en':
                    return enLocale;
                case 'de':
                    return deLocale;
                case 'it':
                    return itLocale;
                case 'hu':
                    return huLocale;
            }
        }
        return slLocale;
    }

    private handleAddNewHoliday() {
        EventBus.$emit(EventBusEvents.openCreateNewEventPopup, {
            popupEvent: EventBusEvents.openCreateNewEventPopup,
            data: {
                eventData: {
                    canEditActivity: this.canEditActivity,
                    canDeleteActivity: this.canDeleteActivity,
                    isOptionalActivity: true, // Holidays are optional activities
                    fromHolidayCalendar: true, // Filter to show only holiday types
                },
            },
        });
    }

    private handleDateClick(arg: any) {
        const selectedDate = arg.dateStr + 'T00:00:00+01:00';

        EventBus.$emit(EventBusEvents.openCreateNewEventPopup, {
            popupEvent: EventBusEvents.openCreateNewEventPopup,
            data: {
                eventData: {
                    localDateFrom: selectedDate,
                    localDateTo: selectedDate,
                    canEditActivity: this.canEditActivity,
                    canDeleteActivity: this.canDeleteActivity,
                    isOptionalActivity: true,
                    fromHolidayCalendar: true,
                },
            },
        });
    }

    private handleEventClick(arg: any) {
        const holidayData = arg.event.extendedProps.holidayData;

        if (!holidayData) {
            return;
        }

        const selectedActivity = NextStepRepository.getById(holidayData.id);

        if (!selectedActivity) {
            // If not in repository, create a minimal object from the holiday data
            EventBus.$emit(EventBusEvents.openCreateNewEventPopup, {
                popupEvent: EventBusEvents.openCreateNewEventPopup,
                data: {
                    eventData: {
                        id: holidayData.id,
                        eventName: holidayData.name,
                        localDateFrom: moment(holidayData.dateFrom).format('YYYY-MM-DDTHH:mm'),
                        localDateTo: moment(holidayData.dateTo).format('YYYY-MM-DDTHH:mm'),
                        eventDescription: holidayData.note || '',
                        activityType: holidayData.type,
                        completed: holidayData.completed,
                        update: true,
                        canEditActivity: this.canEditActivity,
                        canDeleteActivity: this.canDeleteActivity,
                        isOptionalActivity: true,
                        fromHolidayCalendar: true,
                    },
                },
            });
            return;
        }

        EventBus.$emit(EventBusEvents.openCreateNewEventPopup, {
            popupEvent: EventBusEvents.openCreateNewEventPopup,
            data: {
                eventData: {
                    id: selectedActivity.id,
                    eventName: selectedActivity.name,
                    localDateFrom: selectedActivity.dateFrom,
                    localDateTo: selectedActivity.dateTo,
                    eventDescription: selectedActivity.note,
                    activityType: selectedActivity.type,
                    reminderOption: selectedActivity.reminderOption,
                    completed: selectedActivity.completed,
                    update: true,
                    canEditActivity: this.canEditActivity,
                    canDeleteActivity: this.canDeleteActivity,
                    isOptionalActivity: true,
                    fromHolidayCalendar: true,
                },
            },
        });
    }

    private async onSave(eventData: any) {
        try {
            const nextStepInfo = {
                name: eventData.eventName,
                dateFrom: eventData.localDateFrom,
                dateTo: eventData.localDateTo,
                note: eventData.eventDescription,
                completed: eventData.completed || false,
                projectId: null,
                nextStepType: eventData.activityType?.id,
                reminderOption: eventData.reminderOption?.id,
                assignedUser: null,
            };

            await NextStep.createNew(nextStepInfo);
            await this.loadHolidays();

            this.$notification.success({
                message: this.$t('Holiday created successfully') as string,
                description: '',
            });
        } catch (e) {
            this.$notification.error({
                message: this.$t('Error creating holiday') as string,
                description: '',
            });
        }
    }

    private async onUpdate(eventData: any) {
        try {
            const nextStepInfo = {
                name: eventData.eventName,
                dateFrom: eventData.localDateFrom,
                dateTo: eventData.localDateTo,
                note: eventData.eventDescription,
                completed: eventData.completed,
                projectId: null,
                nextStepType: eventData.activityType?.id,
                reminderOption: eventData.reminderOption?.id,
                assignedUser: null,
            };

            await NextStep.updateExisting(eventData.id, nextStepInfo);
            await this.loadHolidays();

            this.$notification.success({
                message: this.$t('Holiday updated successfully') as string,
                description: '',
            });
        } catch (e) {
            this.$notification.error({
                message: this.$t('Error updating holiday') as string,
                description: '',
            });
        }
    }

    private async onDelete(eventData: any) {
        if (!this.canDeleteActivity) {
            this.$notification.error({
                message: this.$t("You don't have user right to delete activities") as string,
                description: '',
            });
            return;
        }

        try {
            await NextStep.deleteExisting(eventData.id);
            await this.loadHolidays();

            this.$notification.success({
                message: this.$t('Holiday deleted successfully') as string,
                description: ``,
            });
        } catch (error) {
            this.$notification.error({
                message: this.$t('Error deleting holiday') as string,
                description: '',
            });
        }
    }

    private async prepareUpdatingActivity(changeType: string, eventData: any) {
        this.isLoading = true;

        switch (changeType) {
            case 'save':
                await this.onSave(eventData);
                break;
            case 'update':
                await this.onUpdate(eventData);
                break;
            case 'delete':
                await this.onDelete(eventData);
                break;
        }

        this.isLoading = false;
    }

    mounted() {
        this.saveActivityHandler = this.prepareUpdatingActivity.bind(this, 'save');
        this.updateActivityHandler = this.prepareUpdatingActivity.bind(this, 'update');
        this.deleteActivityHandler = this.prepareUpdatingActivity.bind(this, 'delete');

        EventBus.$on('on-save-activity', this.saveActivityHandler);
        EventBus.$on('on-update-activity', this.updateActivityHandler);
        EventBus.$on('on-delete-activity', this.deleteActivityHandler);
        EventBus.$on(this.addHolidayEvent, this.handleAddNewHoliday);
    }

    beforeDestroy() {
        EventBus.$off('on-save-activity', this.saveActivityHandler as Function);
        EventBus.$off('on-update-activity', this.updateActivityHandler as Function);
        EventBus.$off('on-delete-activity', this.deleteActivityHandler as Function);
        EventBus.$off(this.addHolidayEvent, this.handleAddNewHoliday);
    }

    @Watch('currentLocale')
    private handleCurrentLocaleChange() {
        this.calendarOptions.locale = this.getLocaleForCalendar();
    }
}
